import { PagenatedResponse, SearchPagenateQuery } from '@models/common';
import {
  LegacyProductStatus,
  LinkedSalesChannelStatus,
  ProductApproveStatus,
  ProductModel,
  ProductStatus,
} from '@models/product';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createSafeStorage, createSizePartialize } from '@utils/localStorage';

const PAGE_SIZE = 20;

interface ProductsContainer {
  contents: ProductModel[];
  page: number;
  size: number;
  total: number;
  hasNext: boolean;
}

export interface ProductModelsQuery extends SearchPagenateQuery {
  buyerId?: string;
  paymentStatus?: string;
  dateType?: string;
  saleType?: string;
  status?: LegacyProductStatus | ProductStatus;
  approveStatus?: ProductApproveStatus;
  stockStatus?: string;
  naverStoreStatus?: LinkedSalesChannelStatus;
  kakaoReservationStatus?: LinkedSalesChannelStatus | 'has_product_name';
  displayStatus?: string;
  sortType?: string;
  appOnly?: boolean;
  mainCampaign?: boolean;
  hasKakaoSalesHistory?: boolean;
}

interface ProductsStoreProps {
  searchResultContainer: ProductsContainer;
  searchQuery: ProductModelsQuery;
  searchKeyword?: string;
  editProductId?: string;
  setEditProductId: (productId?: string) => void;
  setSearchKeyword: (keyword: string) => void;
  updateSearchQuery: (query: { [key: string]: any }) => void;
  resetSearchQuery: () => void;
  setProductsContainer: (result: PagenatedResponse<ProductModel>) => void;
}

export const useProductsStore = create(
  persist<ProductsStoreProps>(
    (set) => ({
      searchResultContainer: {
        contents: [] as ProductModel[],
        total: 0,
        page: 0,
        size: 0,
        hasNext: false,
      },
      searchQuery: {
        page: 0,
        size: PAGE_SIZE,
        saleType: 'all',
      },
      editProductId: undefined,
      setEditProductId: (productId?: string) => {
        set({ editProductId: productId });
      },
      searchKeyword: undefined,
      setProductsContainer: (result) => {
        set((state) => {
          // 메모리 효율성을 위해 실제 데이터만 저장
          const newValue = {
            ...state.searchResultContainer,
            ...result,
            contents: result?.contents || [],
          };
          return { ...state, searchResultContainer: newValue };
        });
      },
      setSearchKeyword: (keyword: string) => {
        set((state) => {
          return {
            ...state,
            searchKeyword: keyword,
            searchResultContainer: { ...state.searchResultContainer, size: 0 },
          };
        });
      },
      updateSearchQuery: (query: { [key: string]: any }) => {
        set((state) => {
          return {
            ...state,
            searchQuery: { ...state.searchQuery, ...query },
            searchResultContainer: { ...state.searchResultContainer, size: 0 },
          };
        });
      },
      resetSearchQuery: () => {
        set({
          searchQuery: {
            page: 0,
            size: PAGE_SIZE,
            saleType: 'all',
            keyword: '',
          },
        });
      },
    }),
    {
      name: 'igogo-admin-products',
      storage: createSafeStorage(),
      partialize: createSizePartialize<ProductsStoreProps>(100),
    },
  ),
);
